"use client";

import React from "react";
import { UserCheck, Zap, Network, ArrowRight, CircleCheck as CheckCircle2, PhoneCall, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const pillars = [{
  icon: UserCheck,
  tag: "Personal accountability",
  title: "Direct access to a senior consultant",
  description: "When you hire me, you work directly with me. No junior account executives, no games of telephone, and no lost context between sales calls and technical delivery.",
  perks: ["Direct communication via call, email, or Slack", "Decisions made by the person executing the work", "Transparent recommendations based strictly on your goals"]
}, {
  icon: Zap,
  tag: "Rapid execution",
  title: "Cost-effective quick-start SEO",
  description: "Agencies often spend months producing slide decks before making a single update. I audit, prioritise, and implement fundamental quick wins from our very first sprint.",
  perks: ["Immediate crawlability and technical triage", "Prioritised search fixes with zero unnecessary fluff", "Clear monthly deliverable milestones and scope"]
}, {
  icon: Network,
  tag: "Long-term growth",
  title: "Scalable content architecture",
  description: "I map your site's information structure, internal link equity, and topical clusters so every article or product page you publish compounds over time.",
  perks: ["Logical URL taxonomy and internal linking models", "Semantic keyword clustering that builds authority", "Future-proof structures designed for AI & organic search"]
}];
const comparisonPoints = [{
  label: "Account handling",
  freelance: "You speak directly with me, Yassine",
  agency: "Junior account manager with high turnover"
}, {
  label: "Billing & overhead",
  freelance: "Fair, transparent pricing for consultant time",
  agency: "Padded retainers covering agency overhead"
}, {
  label: "Strategy & execution",
  freelance: "Unified strategy built and executed by one expert",
  agency: "Strategy created by senior sales, executed by interns"
}, {
  label: "Response speed",
  freelance: "Direct feedback and nimble adjustments",
  agency: "Lengthy sign-off chains and ticketing queues"
}];
export default function Benefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantbenefits" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Subtle decorative background tint */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-40 size-96 rounded-full bg-secondary/60 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -left-40 size-96 rounded-full bg-muted/70 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-default font-medium text-xs tracking-wide uppercase border border-border">
            <Sparkles className="size-3 text-accent mr-1" />
            The independent difference
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Work directly with me, not an agency tier
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            You get senior SEO execution, fast practical setup, and scalable content systems
            without the bloat of agency retainers.
          </p>
        </div>

        {/* 3 Main Pillars */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 flex flex-col justify-between">
                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="size-12 rounded-lg bg-secondary flex items-center justify-center text-primary">
                          <IconComponent className="size-6 text-primary" />
                        </div>
                        <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                          {pillar.tag}
                        </span>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-default font-semibold text-card-foreground">
                        {pillar.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <CardDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {pillar.description}
                      </CardDescription>

                      <div className="pt-3 border-t border-border space-y-2.5">
                        {pillar.perks.map((perk, perkIndex) => <div key={perkIndex} data-index={perkIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-default">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{perk}</span>
                          </div>)}
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Comparative Breakdown Table */}
        <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8 lg:p-10 shadow-xs mb-16">
          <div className="max-w-2xl mb-8">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
              Direct comparison
            </span>
            <h3 className="text-xl sm:text-2xl font-default font-semibold text-foreground mt-1">
              Why clients switch to working directly with me
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground font-default mt-1.5">
              Clear commitments, practical accountability, and zero agency markups.
            </p>
          </div>

          <div className="divide-y divide-border overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-12 pb-3 font-default text-xs uppercase tracking-wider text-muted-foreground">
              <div className="md:col-span-4 hidden md:block">Aspect</div>
              <div className="md:col-span-4 text-primary font-semibold hidden md:block">
                Working with Yassine (YE Consultancy)
              </div>
              <div className="md:col-span-4 hidden md:block">Traditional SEO agency</div>
            </div>

            {comparisonPoints.map((point, index) => <div key={point.label} data-index={index} className="py-4 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center text-sm font-default">
                <div className="md:col-span-4 font-medium text-foreground">
                  {point.label}
                </div>
                <div className="md:col-span-4 flex items-center gap-2 text-foreground font-medium bg-secondary/50 md:bg-transparent p-2.5 md:p-0 rounded-md">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{point.freelance}</span>
                </div>
                <div className="md:col-span-4 text-muted-foreground pl-6 md:pl-0 text-xs sm:text-sm">
                  <span className="md:hidden font-medium text-muted-foreground/80">Agency: </span>
                  {point.agency}
                </div>
              </div>)}
          </div>
        </div>

        {/* Bottom CTA bar */}
        <div className="rounded-xl bg-primary text-primary-foreground p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-md">
          <div className="space-y-1.5 text-center md:text-left">
            <h4 className="text-xl sm:text-2xl font-default font-semibold text-primary-foreground">
              Ready to discuss your search strategy?
            </h4>
            <p className="text-sm sm:text-base text-primary-foreground/85 font-default max-w-xl">
              Book an exploratory consultation with me directly. We will review your search
              priorities without high-pressure sales pitches.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-medium shadow-xs">
              <Link to="/ContactPage">
                <PhoneCall className="size-4 mr-2" />
                Book a Free Consultation
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}